import { Vue, Options } from 'vue-class-component';
import { mapState } from 'vuex';
import {
  ApartmentDTO,
  EntrancePlantDetailDTO,
  ApartmentInvitationDTO,
  ApartmentLabelDTO,
  AddressingType,
} from '@/../client-generator/generated-clients/plants';
import { PropType } from 'vue';
import { getUserInitialsForBubble } from '@/core/utils/utils';
import DialogEditLabel from './DialogEditLabel/DialogEditLabel.vue';
import DialogInviteResident from './DialogInviteResident/DialogInviteResident.vue';
import { SET_APP_LOADING } from '@/core/store/action-types';
import DialogConfirmDelete from '@/views/components/DialogConfirmDelete/DialogConfirmDelete.vue';
import plantsClient from '@/core/services/plants';
import DialogInfo from '@/views/components/DialogInfo/DialogInfo.vue';
import {
  getReferenceDeviceForVirtualDevice,
  sortApartmentLabels,
} from '@/core/utils/EntrancePlant/entrance-plant.utils';

@Options({
  name: 'ContactSection',
  components: {
    DialogEditLabel,
    DialogInfo,
    DialogConfirmDelete,
    DialogInviteResident,
  },
  props: {
    editingApartment: {
      type: Object as PropType<ApartmentDTO>,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant', 'adminView']),
  },
  watch: {
    'editingApartment.scsAddr': function () {
      this.apartmentWasEdited();
    },
    'editingApartment.buildingCode': function () {
      this.apartmentWasEdited();
    },
    'editingApartment.floorCode': function () {
      this.apartmentWasEdited();
    },
    'editingApartment.riserCode': function () {
      this.apartmentWasEdited();
    },
    'editingApartment.apartmentCode': function () {
      this.apartmentWasEdited();
    },
    'editingApartment.code': function () {
      this.verifyValidityOfCallingCode();
      this.apartmentWasEdited();
    },
  },
  emits: ['onAddEditLabel', 'onDeleteLabel', 'update-list', 'contact-edited', 'error-contact'],
})
export default class ContactSection extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  disabled!: boolean;
  adminView!: boolean;
  editingApartment!: ApartmentDTO;
  labelToEdit: ApartmentLabelDTO | null = null;
  invitationToRemove: ApartmentInvitationDTO | null = null;
  isVisibleDialogEditLabel: boolean = false;
  isVisibleDialogInviteResident: boolean = false;
  isEdit: boolean = false;
  isVisibleDeleteInviteConfirm: boolean = false;
  isVisibleMaxEntity: boolean = false;
  noModification: ApartmentDTO = new ApartmentDTO();

  readonly MIN_SCS_ADDRESS = 0;
  readonly MAX_SCS_ADDRESS = 3999;

  scsAddrNotValid: boolean = false;
  invalidScsReason = '';

  codeNotValid: boolean = false;
  invalidCodeReason = '';

  get isFloorsAddressing(): boolean {
    return this.currentEntrancePlant.addressingType === AddressingType.BuildingRiserFloorApartment;
  }

  get isCallWithCode(): boolean {
    return (
      this.currentEntrancePlant.addressingType ===
        AddressingType.DirectCall_Alphanumeric ||
      this.currentEntrancePlant.addressingType ===
        AddressingType.DirectCall_NumbersOnly
    );  
  }

  get currentInvitations() {
    return (
      this.currentEntrancePlant.apartmentInvitations?.filter(
        (i) => i.apartmentId === this.editingApartment.apartmentId ) ?? []
    );
  }

  get sortedLabels() {
    return sortApartmentLabels(this.editingApartment.labels);
  }

  get getContactError() {
    this.verifyValidityOfScsAddress();
    if (this.isCallWithCode) this.verifyValidityOfCallingCode();
    return this.scsAddrNotValid || (this.isCallWithCode && this.codeNotValid);
  }

  get disableInvite() {
    return !this.currentEntrancePlant.syncStatus?.isPlantActive || this.currentInvitations.length  >= 1 || !this.editingApartment.canInvite;
  }

  get revokeInviteMessage() {
    const hasCode = (!!this.editingApartment.keypadCode && this.editingApartment.keypadCode !== '') || this.checkIfHasHiddenGuestLinked();
    if (hasCode) {
      return this.$t('AppResourceEP.ConfirmRemoveInvitedUser');
    } else {
      return this.$t('AppResourceEP.ConfirmRemoveInvitedUser').split('.')[0] + '.';
    }
  }

  checkIfHasHiddenGuestLinked() {
    return this.currentEntrancePlant.hiddenGuests?.some((hg) => hg.scsAddr === this.editingApartment.scsAddr);
  }

  getLabelText(label: ApartmentLabelDTO) {
    if (this.isFloorsAddressing) {
      return this.editingApartment.code;
    }

    if (this.isCallWithCode) {
      let labelText = this.editingApartment.code ?? '';

      if (label.isPrivate && (label.surname || label.name)) {
        labelText += ` |`;
        labelText += ` ${label.surname ?? ''}`;
        labelText += ` ${label.name ?? ''}`;
      } else if (!label.isPrivate && (label.companyName || label.name)) {
        labelText += ` | ${[label.companyName, label.name].filter(Boolean).join(' ')}`;
      }

      return labelText;
    }

    if (label.isPrivate) {
      return `${label.surname ?? ''} ${label.name ?? ''}`;
    } else {
      return [label.companyName, label.name].filter(Boolean).join(' ');
    }
  }

  mounted(): void {
    this.checkIfCanInvite();
    this.noModification = new ApartmentDTO(this.editingApartment);
  }

  checkIfCanAddLabel() {
    if (this.editingApartment.labels!.length >= 10) {
      this.isVisibleMaxEntity = true;
      return;
    }
    this.onAddLabelClick();
  }

  toggleMaxEntityDialog() {
    this.isVisibleMaxEntity = !this.isVisibleMaxEntity;
  }

  onAddLabelClick() {
    this.labelToEdit = null;
    this.isEdit = false;
    this.isVisibleDialogEditLabel = true;
  }

  onEditLabelClick(label: ApartmentLabelDTO) {
    this.labelToEdit = label;
    this.isEdit = true;
    this.isVisibleDialogEditLabel = true;
  }

  onDeleteLabelClick(labelId: string) {
    this.$emit('onDeleteLabel', labelId);
  }

  async onSaveLabel(newLabel: ApartmentLabelDTO) {
    this.hideLabelDialog();
    this.$emit('onAddEditLabel', newLabel);
  }

  userInitials(invitation: ApartmentInvitationDTO): string {
    return getUserInitialsForBubble(invitation);
  }

  hideLabelDialog() {
    this.isVisibleDialogEditLabel = false;
  }

  toggleInviteDialog() {
    this.isVisibleDialogInviteResident = !this.isVisibleDialogInviteResident;
  }

  openDeleteInviteConfirm(invitation: ApartmentInvitationDTO) {
    this.invitationToRemove = invitation;
    this.isVisibleDeleteInviteConfirm = true;
  }

  closeDeleteInviteConfirm() {
    this.isVisibleDeleteInviteConfirm = false;
  }

  async handleDeleteInvite() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      //remove inviteToRemove
      await plantsClient.residentinvitationDELETE(this.currentEntrancePlant.desktopId!, this.invitationToRemove!);
      this.currentEntrancePlant.apartmentInvitations = this.currentEntrancePlant.apartmentInvitations?.filter(
        (i) => i.apartmentId !== this.invitationToRemove!.apartmentId
      );
      if (this.editingApartment.keypadCode) this.editingApartment.keypadCode = undefined;
    } finally {
      this.invitationToRemove = null;
      this.handleUpdateList();
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  handleUpdateList() {
    this.$emit('update-list');
  }

  apartmentWasEdited() {
    this.$emit('error-contact', this.getContactError);
    this.$emit('contact-edited', this.contactEdited());
  }

  contactEdited() {
    const scsEdit = this.editingApartment.scsAddr != this.noModification.scsAddr;
    if(this.isFloorsAddressing) {
      const buildingCodeEdit = this.editingApartment.buildingCode != this.noModification.buildingCode;
      const floorCodeEdit = this.editingApartment.floorCode != this.noModification.floorCode;
      const riserCodeEdit = this.editingApartment.riserCode != this.noModification.riserCode;
      const apartmentCodeEdit = this.editingApartment.apartmentCode != this.noModification.apartmentCode;
      const contactEdit = scsEdit || buildingCodeEdit || floorCodeEdit || riserCodeEdit || apartmentCodeEdit;
      return contactEdit;
    }
    if (this.isCallWithCode) {
      const codeEdit = this.editingApartment.code != this.noModification.code;
      return scsEdit || codeEdit;
    }
    return scsEdit;
  }

  verifyValidityOfScsAddress() {      
    const value = Number(this.editingApartment.scsAddr);
    let acceptedRange = false;
    let doesNotExist = false;

    acceptedRange = value >= this.MIN_SCS_ADDRESS && value <= this.MAX_SCS_ADDRESS;
    if (!acceptedRange) {
      this.scsAddrNotValid = true;
      this.invalidScsReason = this.$t('AppResourceEP.InputNotValid');
      return;
    }

    let listToCheck = this.currentEntrancePlant.buildings!
          .flatMap((b) => b.apartments || [])
    listToCheck = listToCheck.filter((a) => a.apartmentId !== this.editingApartment.apartmentId);
    doesNotExist = !listToCheck.map((a) => a.scsAddr).includes(value);

    if (!doesNotExist) {
      this.scsAddrNotValid = true;
      this.invalidScsReason = this.$t('AppResourceEP.ScsAddressAlreadyInUse');
    } else {
      this.scsAddrNotValid = false;
      this.invalidScsReason = '';
    }
  }

  checkIfCanInvite() {
    const activeGates = this.currentEntrancePlant.gates?.filter(g => getReferenceDeviceForVirtualDevice(g)?.isCommissioned) ?? [];
    this.editingApartment.canInvite = this.currentEntrancePlant.syncStatus?.isPlantActive 
      && this.currentEntrancePlant.syncStatus.areVirtualDevicesSentToEliot
      && activeGates.some(g => g.virtualDevicesApt?.includes(this.editingApartment.apartmentId!))
      && this.currentEntrancePlant.apartmentInvitations?.every(inv => inv.apartmentId !== this.editingApartment.apartmentId);
  }

  verifyValidityOfCallingCode() {
    const callingCode = this.editingApartment.code ?? '';

    const callingCodePattern = this.currentEntrancePlant.addressingType === AddressingType.DirectCall_Alphanumeric
      ? /^[a-zA-Z0-9]{1,9}$/
      : /^\d{1,9}$/;

    if (!callingCode || !callingCodePattern.test(callingCode)) {
      this.codeNotValid = true;
      this.invalidCodeReason = this.currentEntrancePlant.addressingType === AddressingType.DirectCall_Alphanumeric
        ? this.$t('import.errorCallingCodeAlphanumericFormat')
        : this.$t('import.errorCallingCodeNumericFormat');
      return;
    }

    const isCodeAlreadyUsed = this.currentEntrancePlant.buildings!
      .flatMap((building) => building.apartments || [])
      .some((apartment) => apartment.apartmentId !== this.editingApartment.apartmentId
        && apartment.code?.toLowerCase() === callingCode.toLowerCase());

    if (isCodeAlreadyUsed) {
      this.codeNotValid = true;
      this.invalidCodeReason = this.$t('AppResourceEP.AnotherApartmentHasSameCallingCode');
      return;
    }

    this.codeNotValid = false;
    this.invalidCodeReason = '';
  }
}
